import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Typography, Card, Alert, Input, Divider } from 'antd';
import { MinusOutlined, PlusOutlined, PlusCircleOutlined, EditOutlined } from '@ant-design/icons';
import { usePointOfSaleService } from '../../services/pointofsale/PointOfSaleServiceProvider';
import { PointOfSaleItem } from '../../model/pointofsale/PointOfSaleItem';
import { CreatePointOfSaleTransactionRequest } from '../../model/pointofsale/CreatePointOfSaleTransactionRequest';
import { MemberProfile } from '../../model/members/MemberProfile';
import MemberSearchBar from '../common/MemberSearchBar';
import { ReceiptPrinterService } from '../../services/printing/ReceiptPrinterService';
import { useReceiptPrinterService } from '../../services/printing/ReceiptPrinterServiceProvider';
import { USBPrinterSetup } from '../common/USBPrinterSetup';
import { usePosItemSorting } from '../../hooks/usePosItemSorting';
import PosItemSortSelector from '../common/PosItemSortSelector';
import PosItemNameCell from '../common/PosItemNameCell';
import { useFormatCurrency } from '../../hooks/useFormatters';
import PosItemFormModal from './PosItemFormModal';

const { Title, Text } = Typography;

interface PosTerminalProps {
    /**
     * Charge this member instead of searching for one. When set the member search
     * bar is hidden and the basket always belongs to this member — the member
     * profile's "Point of Sale" quick action passes the profile being viewed.
     */
    presetMember?: MemberProfile;
    /**
     * Show the catalogue-management controls (Add Item / edit item) and the USB
     * printer setup. The member-profile modal is a till, not a catalogue editor,
     * so it turns these off.
     */
    allowItemManagement?: boolean;
    /** Fired after a transaction is processed successfully. */
    onTransactionComplete?: () => void;
}

// The till itself: item catalogue on the left, basket + checkout on the right.
// Shared by the full /pos screen and the member-profile POS modal — the only
// difference is where the member comes from (search vs. preset).
const PosTerminal: React.FC<PosTerminalProps> = ({
    presetMember,
    allowItemManagement = true,
    onTransactionComplete,
}) => {
    const posService = usePointOfSaleService();
    const receiptPrinterService = useReceiptPrinterService();
    const [items, setItems] = useState<PointOfSaleItem[]>([]);
    const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(presetMember ?? null);
    const [basket, setBasket] = useState<{ item: PointOfSaleItem; quantity: number; overridePrice: number }[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isItemModalVisible, setIsItemModalVisible] = useState<boolean>(false);
    const [editingItem, setEditingItem] = useState<PointOfSaleItem | null>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const { sortType, handleSortTypeChange, sortItems } = usePosItemSorting();
    const formatCurrency = useFormatCurrency();

    useEffect(() => {
        const loadPosItems = async () => {
            const availableItems = await posService.getPosItems();
            setItems(availableItems);
        };

        // showError rethrows after notifying, so this needs a catch or the failed load
        // surfaces twice: once as the notification, once as an unhandled rejection.
        loadPosItems().catch(() => { /* notified by the API client */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posService]);

    // Keep the till pointed at the profile being viewed if it changes underneath us.
    useEffect(() => {
        if (presetMember) {
            setSelectedMember(presetMember);
        }
    }, [presetMember]);

    const handleQuantityChange = (itemId: string, increment: number) => {
        setBasket(prev => {
            const newBasket = prev.map(basketItem => {
                if (basketItem.item.id === itemId) {
                    const newQuantity = Math.max(0, basketItem.quantity + increment);
                    return { ...basketItem, quantity: newQuantity };
                }
                return basketItem;
            }).filter(item => item.quantity > 0);
            return newBasket;
        });
    };

    const handleAddToBasket = (item: PointOfSaleItem) => {
        setBasket(prev => {
            const existingItem = prev.find(basketItem => basketItem.item.id === item.id);
            if (existingItem) {
                return prev.map(basketItem =>
                    basketItem.item.id === item.id
                        ? { ...basketItem, quantity: basketItem.quantity + 1, overridePrice: basketItem.overridePrice }
                        : basketItem
                );
            }
            return [...prev, { item, quantity: 1, overridePrice: item.price }];
        });
    };

    const calculateTotal = () => {
        return basket.reduce((total, { item, quantity }) => total + item.price * quantity, 0);
    };

    const handleProcessTransaction = async () => {
        const request: CreatePointOfSaleTransactionRequest = {
            membershipId: selectedMember!.membershipId,
            items: basket,
            amount: calculateTotal(),
            bookingSlots: [],
            paymentTransactionStatus: "Completed"
        };
        setIsProcessing(true);
        try {
            const transaction = await posService.makePayment(request);
            const receiptData = ReceiptPrinterService.createReceiptDataFromTransaction(transaction, selectedMember!.name);
            await receiptPrinterService.printReceipt(receiptData, true);
            setBasket([]);
            // A preset member stays selected — the till belongs to that profile.
            setSelectedMember(presetMember ?? null);
            onTransactionComplete?.();
        } catch (error) {
            // The API client has already surfaced the failure; keep the basket so
            // the sale can be retried rather than silently reporting success.
            console.error('Error processing point of sale transaction:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleOpenAddItemModal = () => {
        setEditingItem(null);
        setIsItemModalVisible(true);
    };

    const handleOpenEditItemModal = (item: PointOfSaleItem) => {
        setEditingItem(item);
        setIsItemModalVisible(true);
    };

    const handleItemSaved = (savedItem: PointOfSaleItem) => {
        setItems(prev => {
            const idx = prev.findIndex(i => i.id === savedItem.id);
            if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = savedItem;
                return updated;
            }
            return [...prev, savedItem];
        });
        setIsItemModalVisible(false);
        setEditingItem(null);
    };

    const handleItemModalCancel = () => {
        setIsItemModalVisible(false);
        setEditingItem(null);
    };

    const filteredItems = sortItems(
        items.filter(item =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    return (
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {/* Items List */}
            <div style={{ flex: 2, minWidth: '320px' }}>
                {!presetMember && (
                    <>
                        <MemberSearchBar onMemberSelect={(member) => setSelectedMember(member)} onSelect={() => {}} onRefresh={() => {}} />

                        <Divider style={{ margin: '16px 0' }} />
                    </>
                )}

                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    <Input
                        placeholder="Search items..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ flex: 1, minWidth: '200px' }}
                    />
                    <PosItemSortSelector
                        value={sortType}
                        onChange={handleSortTypeChange}
                    />
                    {allowItemManagement && (
                        <Button
                            type="primary"
                            icon={<PlusCircleOutlined />}
                            onClick={handleOpenAddItemModal}
                        >
                            Add Item
                        </Button>
                    )}
                </div>

                <Divider style={{ margin: '1px 0' }} />

                <Table
                    dataSource={filteredItems}
                    columns={[
                        {
                            title: 'Name',
                            dataIndex: 'name',
                            key: 'name',
                            sorter: (a, b) => a.name.localeCompare(b.name),
                            filters: items.map(item => ({ text: item.name, value: item.name })),
                            onFilter: (value, record) => record.name.includes(value as string),
                            render: (text: string, record: PointOfSaleItem) => (
                                <PosItemNameCell item={record} text={text} />
                            ),
                        },
                        {
                            title: 'Price',
                            dataIndex: 'price',
                            key: 'price',
                            render: (price: number) => formatCurrency(price),
                            sorter: (a, b) => a.price - b.price,
                        },
                        {
                            title: 'Type',
                            dataIndex: 'type',
                            key: 'type',
                            filters: Array.from(new Set(items.map(item => item.type))) // distinct types
                            .map(type => ({ text: type, value: type })), // distinct types
                            onFilter: (value, record) => record.type.includes(value as string),
                        },
                        {
                            title: 'Action',
                            key: 'action',
                            render: (_, record: PointOfSaleItem) => (
                                <Space>
                                    <Button
                                        data-testid={`pos-add-to-basket-${record.id}`}
                                        onClick={() => handleAddToBasket(record)}
                                    >
                                        Add to Basket
                                    </Button>
                                    {allowItemManagement && (
                                        <Button
                                            data-testid={`pos-edit-item-${record.id}`}
                                            aria-label={`Edit ${record.name}`}
                                            icon={<EditOutlined />}
                                            onClick={() => handleOpenEditItemModal(record)}
                                        />
                                    )}
                                </Space>
                            ),
                        },
                    ]}
                    rowKey="id"
                />
                {allowItemManagement && <USBPrinterSetup />}
                <Divider style={{ margin: '16px 0' }} />
            </div>

            {/* Basket Summary */}
            <Card style={{ flex: 1, minWidth: '300px' }}>
                <Title level={3}>Basket</Title>
                <Table
                    dataSource={basket}
                    pagination={false}
                    columns={[
                        {
                            title: 'Item',
                            dataIndex: ['item', 'name'],
                            key: 'name'
                        },
                        {
                            title: 'Price',
                            dataIndex: ['item', 'price'],
                            key: 'price',
                            render: (price: number) => formatCurrency(price)
                        },
                        {
                            title: 'Type',
                            dataIndex: ['item', 'type'],
                            key: 'type'
                        },
                        {
                            title: 'Quantity',
                            key: 'quantity',
                            render: (_, record) => (
                                <Space>
                                    <Button
                                        data-testid={`pos-basket-decrement-${record.item.id}`}
                                        aria-label={`Remove one ${record.item.name}`}
                                        icon={<MinusOutlined />}
                                        onClick={() => handleQuantityChange(record.item.id, -1)}
                                    />
                                    <Text data-testid={`pos-basket-quantity-${record.item.id}`}>
                                        {record.quantity}
                                    </Text>
                                    <Button
                                        data-testid={`pos-basket-increment-${record.item.id}`}
                                        aria-label={`Add one ${record.item.name}`}
                                        icon={<PlusOutlined />}
                                        onClick={() => handleQuantityChange(record.item.id, 1)}
                                    />
                                </Space>
                            )
                        },
                    ]}
                    rowKey={record => record.item.id}
                />

                <div style={{ marginTop: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
                        <Title level={4} style={{ margin: 0 }}>Total:</Title>
                        <Title level={4} style={{ margin: 0 }} data-testid="pos-basket-total">
                            {formatCurrency(calculateTotal())}
                        </Title>
                    </div>
                </div>

                {/* Alert for disabled button */}
                {!selectedMember || basket.length === 0 ? (
                    <Alert
                        message={presetMember
                            ? "Add at least one item to process the transaction"
                            : "A member or guest user must be selected to process the transaction"}
                        type="warning"
                        showIcon
                        style={{ marginBottom: '16px' }}
                    />
                ) : null}

                <Button
                    data-testid="pos-process-transaction"
                    type="primary"
                    onClick={handleProcessTransaction}
                    loading={isProcessing}
                    disabled={!selectedMember || basket.length === 0 || isProcessing}
                    style={{ width: '100%', marginTop: '16px' }}
                >
                    Process Transaction
                </Button>
            </Card>

            {allowItemManagement && (
                <PosItemFormModal
                    open={isItemModalVisible}
                    editingItem={editingItem}
                    existingItems={items}
                    onSaved={handleItemSaved}
                    onCancel={handleItemModalCancel}
                />
            )}
        </div>
    );
};

export default PosTerminal;
